import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const isConfigured = Boolean(supabaseUrl && supabaseAnonKey);

function createAuthStorage() {
  let memoryStore: Record<string, string> = {};
  const safeSessionStorage =
    typeof window !== 'undefined' && window.sessionStorage
      ? window.sessionStorage
      : null;

  return {
    getItem: (key: string) => {
      try {
        return safeSessionStorage?.getItem(key) ?? memoryStore[key] ?? null;
      } catch {
        return memoryStore[key] ?? null;
      }
    },
    setItem: (key: string, value: string) => {
      try {
        safeSessionStorage?.setItem(key, value);
      } catch {
        memoryStore[key] = value;
      }
    },
    removeItem: (key: string) => {
      try {
        safeSessionStorage?.removeItem(key);
      } catch {
        delete memoryStore[key];
      }
    },
  };
}

const baseClient = isConfigured
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        storage: createAuthStorage(),
      },
    })
  : null;

export function getSupabaseClient(): SupabaseClient | null {
  return baseClient;
}

export async function ensureSupabaseSession(): Promise<SupabaseClient | null> {
  if (!baseClient) return null;

  // getSession can hang (e.g. a stale stored session whose refresh never
  // settles), which used to leave callers like the mod login stuck forever.
  const withTimeout = <T,>(promise: Promise<T>, ms: number) =>
    Promise.race([promise, new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Supabase session timed out')), ms))]);

  try {
    const { data, error } = await withTimeout(baseClient.auth.getSession(), 8000);
    if (error) {
      console.error('Supabase session error:', error);
      return baseClient;
    }

    if (!data.session) {
      const { error: signInError } = await withTimeout(baseClient.auth.signInAnonymously(), 8000);
      if (signInError) {
        console.error('Supabase anonymous sign-in error:', signInError);
      }
    }
  } catch (err) {
    console.error('Supabase session setup failed:', err);
  }

  return baseClient;
}

// Helper to check if Supabase is configured
export const isSupabaseConfigured = () => isConfigured;
